import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobal } from '../useGlobal';
import { useDebounce } from '../useDebounce';

/**
 * Merge the global query-state keys into a search string, keeping everything
 * else that is already there.
 *
 * The scope keys (`org`, `ws`) have to survive a pathname change, but they are
 * not the only thing in the URL: a screen's own keys are written by
 * `setTheValue` below, and the details screens put their open tab in `screen`.
 * Rebuilding the search from the global keys alone dropped those, so arriving on
 * `/experiments/5898?screen=statistics` immediately rewrote the URL to
 * `/experiments/5898?org=1&ws=2` and the tab was lost on reload or on share.
 */
const mergeGlobalParams = (search: string, globalEntries: [string, string][]) => {
  const params = new URLSearchParams(search);
  for (const [key, value] of globalEntries) params.set(key, value);
  return params.toString();
};

export const useQueryState = <T extends string | number | null>(key: string, defaultValue?: T, isGlobal = false) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLocation, setCurrentLocation] = useGlobal<typeof location>('LOCATION', location);

  const parseValue = (val: string | null): T => {
    if (val === null) return defaultValue as T;
    if (typeof defaultValue === 'number') return Number(val) as T;
    return val as T;
  };

  useDebounce(
    () => {
      if (!location || !currentLocation) return;
      if (location.pathname === currentLocation.pathname) return;

      const globalEntries = queryClient
        .getQueryCache()
        .getAll()
        .filter((cache) => cache.queryKey[1] === 'QUERY_STATE')
        .map((cache) => [cache.queryKey[2], cache.state.data] as const)
        .filter((entry): entry is [string, string | number] => typeof entry[0] === 'string' && entry[1] != null)
        .map(([entryKey, value]) => [entryKey, String(value)] as [string, string]);

      const search = mergeGlobalParams(window.location.search, globalEntries);
      const url = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;

      setCurrentLocation(location);
      if (url === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;

      /**
       * Replace, never push.
       *
       * This is a correction to the URL the reader is already on - it carries the
       * scope keys across a pathname change - not somewhere they asked to go. As a
       * push it appended a duplicate entry on every single navigation, so the first
       * Back press landed on the same screen and the button looked dead. Going back
       * re-ran it and pushed a duplicate of the entry just returned to, which is why
       * Back needed two presses everywhere and never escaped some screens at all.
       */
      navigate(url, { replace: true });
    },
    100,
    [location],
  );

  const [value, setValue] = isGlobal
    ? useGlobal(['QUERY_STATE', key], () => {
        const searchParams = new URLSearchParams(window.location.search);
        return parseValue(searchParams.get(key));
      })
    : useState<T>(() => {
        const searchParams = new URLSearchParams(window.location.search);
        return parseValue(searchParams.get(key));
      });

  const setTheValue: React.Dispatch<React.SetStateAction<T>> = (arg) => {
    const newValue = typeof arg === 'function' ? arg(value as T) : arg;

    const searchParams = new URLSearchParams(window.location.search);
    if (newValue !== null && newValue !== undefined) {
      searchParams.set(key, String(newValue));
    } else {
      searchParams.delete(key);
    }

    const url = `${window.location.pathname}?${searchParams.toString()}${window.location.hash}`;
    /**
     * Preserve the existing history state, do not replace it with `{}`.
     *
     * React Router keeps its own bookkeeping in `history.state` - `{ usr, key, idx }`
     * - and `idx` is how it knows where in the stack it is. Passing `{}` wiped that,
     * so after any filter, tab or pagination change (all of which run through here)
     * the router no longer knew its position and `navigate(-1)` stopped going back.
     * That is why the toolbar's back and forward chevrons appeared dead: the URL was
     * fine, the history entry it pointed at had been stripped.
     */
    window.history.replaceState(window.history.state, '', url);
    setValue(newValue);
  };

  const reset = () => setTheValue(defaultValue as T);
  const clear = () => setTheValue(null as T);

  return [value as T, setTheValue, reset, clear] as const;
};
